export const expectedPagesByVertical = {
    academy: ["home", "programs", "coaches", "admissions", "contact"],
    fitness: ["home", "programs", "trainers", "membership", "contact"],
    commerce: ["home", "products", "collections", "contact"],
    realEstate: ["home", "listings", "services", "contact"],
    realEstatePortal: ["home", "listings", "cities", "contact"],
    healthcare: ["home", "treatments", "doctors", "contact"],
    legal: ["home", "practice-areas", "team", "contact"],
    restaurant: ["home", "menu", "specials", "gallery", "contact"],
    beauty: ["home", "services", "specialists", "gallery", "contact"],
    saas: ["home", "features", "pricing", "integrations", "contact"]
};

export function normalizePageKey(value) {
    if (!value) return "";

    let key = String(value).trim().toLowerCase();

    key = key.replace(/^\/+/, "");
    key = key.replace(/^generated-sites\/[^/]+\//, "");
    key = key.replace(/^sites\/[^/]+\//, "");
    key = key.replace(/\?.*$/, "");
    key = key.replace(/#.*$/, "");
    key = key.replace(/\.html$/, "");
    key = key.replace(/\/$/, "");

    if (!key || key === "index" || key === "home.html") return "home";

    return key;
}

export function pageKeyToFileName(key) {
    const normalized = normalizePageKey(key);
    return normalized === "home" ? "index.html" : `${normalized}.html`;
}

export function normalizeGeneratedPages(pages) {
    if (!Array.isArray(pages)) return [];

    return pages
        .map((page) => {
            const key =
                page.key ||
                page.pageKey ||
                page.slug ||
                page.name ||
                page.title ||
                "";

            const normalizedKey = normalizePageKey(key);

            return {
                key: normalizedKey,
                title: page.title || page.name || normalizedKey || "Page",
                fileName: page.fileName || page.path || pageKeyToFileName(normalizedKey),
                raw: page
            };
        })
        .filter((x) => x.key);
}

export function extractHrefLinks(html) {
    if (!html) return [];

    const links = [];
    const regex = /<a\b[^>]*\bhref\s*=\s*["']([^"']+)["'][^>]*>/gi;

    let match;
    while ((match = regex.exec(html)) !== null) {
        const href = match[1];

        if (!href) continue;
        if (href.startsWith("#")) continue;
        if (href.startsWith("mailto:")) continue;
        if (href.startsWith("tel:")) continue;
        const scriptProtocol = ["java", "script:"].join("");
        if (href.toLowerCase().startsWith(scriptProtocol)) continue;
        if (/^https?:\/\//i.test(href)) continue;

        links.push({
            href,
            pageKey: normalizePageKey(href),
            fileName: pageKeyToFileName(href)
        });
    }

    return Array.from(
        new Map(links.map((x) => [x.href, x])).values()
    );
}

export function extractUsedClasses(html) {
    if (!html) return [];

    const classes = new Set();
    const regex = /\bclass\s*=\s*["']([^"']+)["']/gi;

    let match;
    while ((match = regex.exec(html)) !== null) {
        String(match[1])
            .split(/\s+/)
            .map((x) => x.trim())
            .filter(Boolean)
            .forEach((x) => classes.add(x));
    }

    return Array.from(classes).sort();
}

export function extractDefinedCssClasses(cssText) {
    if (!cssText) return [];

    const classes = new Set();
    const regex = /\.([a-zA-Z_-][a-zA-Z0-9_-]*)[\s:{,.#>+~[]/g;

    let match;
    while ((match = regex.exec(cssText)) !== null) {
        classes.add(match[1]);
    }

    return Array.from(classes).sort();
}

export function extractInlineCss(html) {
    if (!html) return "";

    const styleBlocks = [];
    const regex = /<style\b[^>]*>([\s\S]*?)<\/style>/gi;

    let match;
    while ((match = regex.exec(html)) !== null) {
        styleBlocks.push(match[1]);
    }

    return styleBlocks.join("\n\n");
}

export function extractCssLinks(html) {
    if (!html) return [];

    const links = [];
    const regex = /<link\b[^>]*rel\s*=\s*["']stylesheet["'][^>]*>/gi;
    const hrefRegex = /\bhref\s*=\s*["']([^"']+)["']/i;

    let match;
    while ((match = regex.exec(html)) !== null) {
        const tag = match[0];
        const hrefMatch = hrefRegex.exec(tag);

        if (hrefMatch?.[1]) {
            links.push(hrefMatch[1]);
        }
    }

    return links;
}

export function extractScriptLinks(html) {
    if (!html) return [];

    const links = [];
    const regex = /<script\b[^>]*\bsrc\s*=\s*["']([^"']+)["'][^>]*>/gi;

    let match;
    while ((match = regex.exec(html)) !== null) {
        links.push(match[1]);
    }

    return links;
}

export function extractCalledJsFunctions(html) {
    if (!html) return [];

    const functions = new Set();

    const eventRegex = /\bon[a-z]+\s*=\s*["']([^"']+)["']/gi;
    let eventMatch;

    while ((eventMatch = eventRegex.exec(html)) !== null) {
        const handler = eventMatch[1];
        const fnRegex = /\b([a-zA-Z_$][a-zA-Z0-9_$]*)\s*\(/g;
        let fnMatch;

        while ((fnMatch = fnRegex.exec(handler)) !== null) {
            functions.add(fnMatch[1]);
        }
    }

    return Array.from(functions).sort();
}

export function extractDefinedJsFunctions(html, jsText = "") {
    const source = `${html || ""}\n${jsText || ""}`;
    const functions = new Set();

    const patterns = [
        /function\s+([a-zA-Z_$][a-zA-Z0-9_$]*)\s*\(/g,
        /const\s+([a-zA-Z_$][a-zA-Z0-9_$]*)\s*=\s*\(/g,
        /let\s+([a-zA-Z_$][a-zA-Z0-9_$]*)\s*=\s*\(/g,
        /var\s+([a-zA-Z_$][a-zA-Z0-9_$]*)\s*=\s*\(/g,
        /window\.([a-zA-Z_$][a-zA-Z0-9_$]*)\s*=/g
    ];

    patterns.forEach((regex) => {
        let match;
        while ((match = regex.exec(source)) !== null) {
            functions.add(match[1]);
        }
    });

    return Array.from(functions).sort();
}

export function runGenerationQa({
    verticalKey,
    html,
    cssText,
    jsText,
    generatedPages
}) {
    const normalizedPages = normalizeGeneratedPages(generatedPages);
    const generatedPageKeys = normalizedPages.map((x) => x.key);

    const expectedPages = expectedPagesByVertical[verticalKey] || ["home"];

    const navLinks = extractHrefLinks(html);
    
    const missingExpectedPages = expectedPages.filter(
        (pageKey) => !generatedPageKeys.includes(pageKey)
    );

    const brokenNavbarLinks = navLinks.filter(
        (link) =>
            link.pageKey &&
            !generatedPageKeys.includes(link.pageKey) &&
            link.pageKey !== "home"
    );

    const inlineCss = extractInlineCss(html);
    const mergedCss = [cssText, inlineCss].filter(Boolean).join("\n\n");

    const usedClasses = extractUsedClasses(html);
    const definedClasses = extractDefinedCssClasses(mergedCss);

    const missingCssClasses =
        definedClasses.length === 0
            ? []
            : usedClasses.filter((className) => !definedClasses.includes(className));

    const cssLinks = extractCssLinks(html);
    const scriptLinks = extractScriptLinks(html);

    const hasMediaQuery = /@media\s*\(/i.test(mergedCss);
    const hasViewportMeta = /<meta\b[^>]*name\s*=\s*["']viewport["']/i.test(html);
    const hasHamburger =
        /hamburger|mobile-menu|menu-toggle|nav-toggle/i.test(html);

    const calledFunctions = extractCalledJsFunctions(html);
    const definedFunctions = extractDefinedJsFunctions(html, jsText);

    const missingJsHandlers = calledFunctions.filter(
        (fn) => !definedFunctions.includes(fn)
    );

    const warnings = [];
    const errors = [];

    if (missingExpectedPages.length > 0) {
        errors.push(
            `Missing expected pages: ${missingExpectedPages
                .map(pageKeyToFileName)
                .join(", ")}`
        );
    }

    if (brokenNavbarLinks.length > 0) {
        errors.push(
            `Broken navbar links: ${brokenNavbarLinks
                .map((x) => x.href)
                .join(", ")}`
        );
    }

    if (cssLinks.length === 0 && !inlineCss) {
        warnings.push("No stylesheet link or inline CSS found.");
    }

    if (!hasViewportMeta) {
        warnings.push("Missing mobile viewport meta tag.");
    }

    if (!hasMediaQuery) {
        warnings.push("No CSS media queries found. Mobile support may be weak.");
    }

    if (!hasHamburger && navLinks.length > 4) {
        warnings.push("Navbar has many links but no obvious mobile menu/hamburger markup.");
    }

    if (missingCssClasses.length > 0) {
        warnings.push(
            `${missingCssClasses.length} HTML classes are not found in available CSS.`
        );
    }

    if (missingJsHandlers.length > 0) {
        errors.push(
            `Missing JS handlers: ${missingJsHandlers.join(", ")}`
        );
    }

    const status =
        errors.length > 0 ? "failed" : warnings.length > 0 ? "warning" : "passed";

    return {
        status,
        errors,
        warnings,
        expectedPages,
        generatedPages: normalizedPages,
        generatedPageKeys,
        navLinks,
        brokenNavbarLinks,
        missingExpectedPages,
        cssLinks,
        scriptLinks,
        usedClasses,
        definedClasses,
        missingCssClasses,
        hasMediaQuery,
        hasViewportMeta,
        hasHamburger,
        calledFunctions,
        definedFunctions,
        missingJsHandlers
    };
}